import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  InfoLabel,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Switch,
  type LabelProps,
} from '@fluentui/react-components';
import { CopyRegular } from '@fluentui/react-icons';
import { dashboardsService } from '../api/dashboardsService';

interface DashboardShareDialogProps {
  dashboardId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function createDashboardEmbedHtml(embedUrl: string): string {
  const url = new URL(embedUrl);
  url.searchParams.set('showHeader', 'true');
  url.searchParams.set('showFilters', 'true');
  const escapedUrl = url.toString()
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<iframe src="${escapedUrl}" title="Embedded dashboard" loading="lazy" allowfullscreen style="position:fixed;inset:0;width:100%;height:100%;border:0"></iframe>`;
}

export const DashboardShareDialog: React.FC<DashboardShareDialogProps> = ({
  dashboardId,
  open,
  onOpenChange,
}) => {
  const [enabled, setEnabled] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setShareUrl(null);
    setEmbedUrl(null);
    dashboardsService.getShareInfo(dashboardId)
      .then((info) => {
        if (!cancelled) {
          setEnabled(info.enabled);
          setShareUrl(info.shareUrl);
          setEmbedUrl(info.embedUrl);
        }
      })
      .catch(() => { if (!cancelled) setError('Sharing settings could not be loaded.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dashboardId, open]);

  const updateSharing = async (nextEnabled: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const result = await dashboardsService.shareDashboard(dashboardId, { enabled: nextEnabled });
      setEnabled(result.enabled);
      setShareUrl(result.shareUrl);
      setEmbedUrl(result.embedUrl);
    } catch {
      setError('Sharing settings could not be updated.');
    } finally {
      setLoading(false);
    }
  };

  const resetLink = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await dashboardsService.resetShareLink(dashboardId);
      setEnabled(result.enabled);
      setShareUrl(result.shareUrl);
      setEmbedUrl(result.embedUrl);
      setConfirmingReset(false);
    } catch {
      setError('The share link could not be reset.');
    } finally {
      setLoading(false);
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      setError('The value could not be copied. Select and copy it manually.');
    }
  };

  return <>
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Share dashboard</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
            {loading && <Spinner size="small" label="Updating sharing" />}
            <Switch
              label="Anyone with the link can view"
              checked={enabled}
              disabled={loading}
              onChange={(_, data) => void updateSharing(data.checked)}
            />
            {shareUrl && (
              <Field label="Share link">
                <Input readOnly value={shareUrl} contentAfter={<Button appearance="subtle" icon={<CopyRegular />} aria-label="Copy share link" onClick={() => void copy(shareUrl)} />} />
              </Field>
            )}
            {embedUrl && (
              <Field label={{ children: (_: unknown, props: LabelProps) => (
                <InfoLabel {...props} infoButton={{ 'aria-label': 'About dashboard embeds' }}
                  info={<>Embed mode is read-only and does not require sign-in. In the iframe URL, set <strong>showHeader</strong> and <strong>showFilters</strong> to true or false to control whether the dashboard title and filters are shown.</>}>
                  Embed code
                </InfoLabel>
              ) }}>
                <Input readOnly value={createDashboardEmbedHtml(embedUrl)} contentAfter={<Button appearance="subtle" icon={<CopyRegular />} aria-label="Copy embed code" onClick={() => void copy(createDashboardEmbedHtml(embedUrl))} />} />
              </Field>
            )}
          </DialogContent>
          <DialogActions>
            {enabled && <Button disabled={loading} onClick={() => setConfirmingReset(true)}>Reset link</Button>}
            <Button appearance="primary" onClick={() => onOpenChange(false)}>Done</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    <Dialog open={confirmingReset} onOpenChange={(_, data) => setConfirmingReset(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Reset share link?</DialogTitle>
          <DialogContent>
            Existing share links and embeds will stop working immediately. This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button disabled={loading} onClick={() => setConfirmingReset(false)}>Cancel</Button>
            <Button appearance="primary" disabled={loading} onClick={() => void resetLink()}>Reset link</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  </>;
};